import PropTypes from 'prop-types'
import { isValidElement } from 'react'

import Card from '@/components/Card'

import './index.scss'

import { FormattedMessage } from 'react-intl'

import Button from '@/components/Button'

import ErrorEffect from './ErrorEffect'
import messages from './messages'

const genericErrorMessage = {
  title: messages.genericTitle,
  message: messages.genericMessage
}
const statusErrorMessage = {
  400: {
    title: messages.genericBadRequestErrorTitle,
    message: messages.genericBadRequestErrorMessage
  },
  401: {
    title: messages.genericUnauthorizedErrorTitle,
    message: messages.genericUnauthorizedErrorMessage
  },
  403: {
    title: messages.genericUnauthorizedErrorTitle,
    message: messages.genericUnauthorizedErrorMessage
  },
  404: {
    title: messages.genericNotFoundErrorTitle,
    message: messages.genericNotFoundErrorMessage
  },
  500: {
    title: messages.genericInternalServerErrorTitle,
    message: messages.genericInternalServerErrorMessage
  },
  502: {
    title: messages.genericInternalServerErrorTitle,
    message: messages.genericInternalServerErrorMessage
  },
  504: {
    title: messages.genericGatewayTimeoutErrorTitle,
    message: messages.genericGatewayTimeoutErrorMessage
  }
}

const errorShape = PropTypes.shape({
  type: PropTypes.string,
  code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  httpStatusCode: PropTypes.number,
  detailCode: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.element]),
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.element]),
  subMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.element]),
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      actionMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.element]),
      onAction: PropTypes.func
    })
  ),
  useDefaults: PropTypes.bool
})

const Index = ({ errors, reportErrors }: any) => {
  if (!errors || errors.length === 0) return null

  const [
    { title, message, subMessage, actions, code, httpStatusCode, detailCode = 'ERR', useDefaults = true },
    ...otherErrors
  ] = errors

  // @ts-expect-error
  const defaultErrorMessage = statusErrorMessage[httpStatusCode] || genericErrorMessage

  const titleElement =
    typeof title === 'string' || isValidElement(title) ? (
      title
    ) : (
      <FormattedMessage {...(title || defaultErrorMessage.title)} />
    )

  //use the message provided if it is a string, element, or object, otherwise if using defaults and there is a default use that
  const messageElement =
    (!message && (!useDefaults || !defaultErrorMessage.message)) ||
    typeof message === 'string' ||
    isValidElement(message) ? (
      message
    ) : (
      <FormattedMessage {...(message || defaultErrorMessage.message)} />
    )

  //use the submessage provided if it is a string, element, or object, otherwise if using defaults and there is a default use that
  const subMessageElement =
    (!subMessage && (!useDefaults || !defaultErrorMessage.subMessage)) ||
    typeof subMessage === 'string' ||
    isValidElement(subMessage) ? (
      subMessage
    ) : (
      <FormattedMessage {...(subMessage || defaultErrorMessage.subMessage)} />
    )

  return (
    <>
      {reportErrors !== false && <ErrorEffect errors={errors} />}
      <Card className="error-card">
        <Card.Content className="center">
          <div className="workgrid-logo" />
          <div className="error-title">{titleElement}</div>
          {!!messageElement && <div className="error-message">{messageElement}</div>}
          {!!subMessageElement && <div className="error-submessage">{subMessageElement}</div>}
          <a href="https://help.workgrid.com/s/contactsupport" target="_blank" rel="noopener noreferrer">
            <FormattedMessage {...messages.genericWorkgridSupportMessage} />
          </a>
        </Card.Content>
        {actions && actions.length > 0 && (
          <Card.Footer className="center">
            {actions.map(({ actionMessage, onAction }: any, index: any) => {
              const actionMessageElement =
                !actionMessage || typeof actionMessage === 'string' || isValidElement(actionMessage) ? (
                  actionMessage
                ) : (
                  <FormattedMessage {...actionMessage} />
                )
              const action = onAction || (() => {})

              return (
                <Button key={`action-${index}`} color="tertiary" onClick={action}>
                  {actionMessageElement}
                </Button>
              )
            })}
          </Card.Footer>
        )}
        <div className="error-details">
          <div className="detail-code">{`${detailCode}-${code}`}</div>
          {otherErrors.map(({ code, detailCode = 'ERR' }: any, index: any) => (
            <div key={`error-${index}`} className="detail-code">{`${detailCode}-${code}`}</div>
          ))}
        </div>
      </Card>
    </>
  )
}

Index.displayName = 'ErrorComponent'

Index.propTypes = {
  errors: PropTypes.arrayOf(errorShape).isRequired,
  reportErrors: PropTypes.bool
}

Index.defaultProps = {}

export default Index
